import React, { createContext, useContext, useState } from 'react';

const TranslationContext = createContext();

export const useTranslation = () => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
};

const translations = {
  en: {
    // Header
    companyName: "Ramson Feeds",
    tagline: "Quality Cattle Feed & Raw Materials",
    callUs: "Call Us",
    
    // Navigation
    home: "Home",
    about: "About Us",
    gallery: "Gallery",
    products: "Products",
    contact: "Contact Us",
    
    // Common
    learnMore: "Learn More",
    viewAll: "View All",
    sendMessage: "Send Message",
    orderNow: "Order Now",
    enquireNow: "Enquire Now",
    quantity: "Quantity",
    bags: "bags",
    submitOrder: "Submit Order via WhatsApp",
    
    // Home Page
    welcome: "Welcome to Ramson Feeds",
    welcomeDesc: "Providing high-quality cattle feed and raw materials to farmers and farms and shops for over 25 years. Our products are formulated to ensure optimal health and productivity for your livestock.",
    exploreProducts: "Explore Our Products",
    ourProducts: "Our Products",
    discoverRange: "Discover our range of high-quality cattle feed and raw materials",
    cattleFeed: "Cattle Feed",
    rawMaterials: "Raw Materials",
    whyChooseUs: "Why Choose Ramson Feeds?",
    whyChooseDesc: "With over two decades of experience in the industry, we've built a reputation for quality and reliability that farmers trust.",
    qualityCertified: "99% Farmer Satisfaction",
    fastDelivery: "Consistent Nutrition",
    expertSupport: "Always Stocked",
    allProductsMeet: "Trusted by generations.",
    timelyDelivery: "Every batch balanced for healthier cattle and higher yield.",
    technicalGuidance: "Reliable supply, no matter the season.",
    featuredProducts: "Featured Products",
    featuredDesc: "Some of our most popular cattle feed products",
    
    // About Page
    aboutHero: "About Ramson Feeds",
    aboutTagline: "Your trusted partner in cattle nutrition for over 25 years",
    ourStory: "Our Story",
    story1: "Founded in 2000, Ramson Feeds began as a small family-owned operation with a simple mission: to provide local farmers with high-quality, affordable cattle feed. What started as a modest venture has grown into one of the region's most trusted suppliers of cattle nutrition products.",
    story2: "Today, we serve hundreds of farms and shop across the states, but we've never lost sight of our roots. We still operate with the same family values and commitment to quality that defined us from the beginning.",
    ourMission: "Our Mission",
    ourVision: "Our Vision",
    missionText: "To enhance livestock productivity and farmer profitability through specially formulated, high-quality feed products that promote animal health and welfare.",
    visionText: "To be the leading provider of innovative nutritional solutions for the livestock industry, recognized for our quality, reliability, and customer commitment.",
    ourValues: "Our Values",
    valuesDesc: "The principles that guide everything we do",
    quality: "Quality",
    innovation: "Innovation",
    community: "Collaboration",
    integrity: "Integrity",
    qualityd: "Uncompromising excellence",
    innovationd: "Creative solutions ahead",
    integrityd: "Honesty in action",
    communityd: "Stronger together",
    benefitsTitle: "Benefits of Our Cattle Feed",
    benefitsDesc: "Why farmers choose Ramson Feeds for their livestock",
    improvedHealth: "Improved Cattle Nutrition",
    enhancedProductivity: "Enhanced Milk Production",
    costEffective: "Cost-Effective",
    naturalIngredients: "Natural Ingredients",
    yearsExperience: "Years of Experience",
    satisfiedCustomers: "Satisfied Customers",
    productsAvailable: "Products",
    customerSupport: "Result",
    
    // Products Page
    productsHero: "Our Products",
    productsDesc: "High-quality cattle feed and raw materials for optimal livestock health",
    nutritionallyBalanced: "Nutritionally balanced feeds for all stages of cattle development",
    highQualityIngredients: "High-quality ingredients for custom feed formulations",
    placeOrder: "Place Your Order",
    orderDesc: "Fill out the form below and we'll contact you via WhatsApp to confirm your order.",
    fullName: "Full Name",
    whatsappNumber: "WhatsApp Number",
    productInterest: "Product of Interest",
    enterProductName: "Enter product name",
    selectedItems: "Selected Items",
    totalItems: "Total Items",
    clearSelection: "Clear Selection",
    orderProcess: "How Our Order Process Works",
    processStep1: "Fill out the order form with your requirements",
    processStep2: "We'll contact you via WhatsApp to confirm details",
    processStep3: "Receive order confirmation and delivery timeline",
    processStep4: "Enjoy timely delivery of your quality feed materials",
    toor: "Toor Husk",
    orid: "Orid Dust",
    rg: "Roasted gram Husk",
    moong: "Moong Dust",
    maize: "Maize",
    masoor: "Masoor Dal",
    kappi: "",
    salt: "Salt",
    ricebran: "Rice Bran",
    broad: "Broad Beans",
    sc: "Groundnut Sead Cake",
    pellete: "Pellete",
    ricef: "Rice Flour",
    pmaize: "Powdered Maize",

    toord: "Energy-rich husk",
    oridd: "Protein-rich powder",
    rgd: "Fiber source husk",
    moongd: "Mineral-rich powder",
    masoord: "Vitamin source dal",
    maized: "Energy grain",
    ricebd: "Digestive energy source",
    broadd: "Protein-rich beans",
    scd: "Protein and oil source",
    pelleted: " Energy feed pellet",
    ricefbd: "Fine energy powder",
    pmaized: "Palatable maize powder",
    kappid: "Fiber-rich residue",
    saltd: "Mineral balance",

    maan: "Maan Mixed Feed",
    komatha: "Komatha Mixed Feed",
    goat: "Ramson Goat Feed",
    ramachandra: "Ramachandra Toor Feed",
    ivory: "60Kg Ramachandra Toor Feed",
    no2: "No.2 Toor Feed",
    spkomatha: "Komatha Spl Toor Feed",
    srtc: "SRTC Orid Feed",
    smr: "SMR Orid Feed",
    apple: "Apple Lentil Feed",
    mgs: "MGS Moong Feed",
    appu: "Appu Broad Bean Feed",

    // Contact Page
    contactHero: "Contact Us",
    contactDesc: "Get in touch with us for inquiries, orders, or support",
    sendUsMessage: "Send us a Message",
    getInTouch: "Get in Touch",
    visitOffice: "Visit Our Factory",
    businessHours: "Business Hours",
    emergencyContact: "Emergency Contact",
    emergencyDesc: "For urgent orders or support outside business hours:",
    verifyHuman: "Verify you're human",
    enterCode: "Enter the code above",
    faq: "Frequently Asked Questions",
    faqDesc: "Quick answers to common questions",
    emailUs: "Email Us",
    ourLocation: "Our Location",
    message: "Message",
    tellUsHow: "Enter your message",
    deliveryArea: "Delivery Area",
    deliveryAreaAnswer: "We deliver across all parts of India.",
    bulkDiscounts: "Bulk Discounts",
    bulkDiscountsAnswer: "Bulk discounts are available for large quantity orders.",
    customFormulations: "Custom Formulations",
    customFormulationsAnswer: "We offer custom formulations. A minimum order of 100 bags is required.",
    paymentMethods: "Payment Methods",
    paymentMethodsAnswer: "We accept advance payments through any preferred mode.",


    // Footer
    quickLinks: "Quick Links",
    contactInfo: "Contact Info",
    rightsReserved: "All Rights Reserved",
    companyDescription: "Growth! Milk! Strength!"
  },
  ta: {
    // Header
    companyName: "ராம்சன் ஃபீட்ஸ்",
    tagline: "தரமான கால்நடை தீவனம் மற்றும் மூலப்பொருட்கள்",
    callUs: "அழைக்கவும்",
    
    // Navigation
    home: "முகப்பு",
    about: "எங்களைப் பற்றி",
    gallery: "கேலரி",
    products: "தயாரிப்புகள்",
    contact: "தொடர்பு கொள்ள",
    
    // Common
    learnMore: "மேலும் அறிக",
    viewAll: "அனைத்தையும் காண்க",
    sendMessage: "செய்தி அனுப்புக",
    orderNow: "உடனே ஆர்டர் செய்க",
    enquireNow: "விசாரிக்கவும்",
    quantity: "அளவு",
    bags: "பைகள்",
    submitOrder: "WhatsApp மூலம் ஆர்டர் அனுப்புக",
    
    // Home Page
    welcome: "ராம்சன் ஃபீட்ஸ்க்கு வரவேற்கிறோம்",
    welcomeDesc: "25 ஆண்டுகளுக்கும் மேலாக விவசாயிகள் மற்றும் பண்ணையார்களுக்கு மற்றும் கடைகளுக்கு உயர்தர கால்நடை தீவனம் மற்றும் மூலப்பொருட்களை வழங்குகிறோம். உங்கள் கால்நடைகளின் உகந்த ஆரோக்கியம் மற்றும் உற்பத்தித்திறனை உறுதிப்படுத்த எங்கள் தயாரிப்புகள் வடிவமைக்கப்பட்டுள்ளன.",
    exploreProducts: "எங்கள் தயாரிப்புகளை ஆராய்க",
    ourProducts: "எங்கள் தயாரிப்புகள்",
    discoverRange: "எங்கள் உயர்தர கால்நடை தீவனம் மற்றும் மூலப்பொருட்களின் வரம்பைக் கண்டறியவும்",
    cattleFeed: "கால்நடை தீவனம்",
    rawMaterials: "மூலப்பொருட்கள்",
    whyChooseUs: "ஏன் ராம்சன் ஃபீட்ஸை தேர்வு செய்ய வேண்டும்?",
    whyChooseDesc: "இந்தத் தொழிலில் இருபது ஆண்டுகளுக்கும் மேலான அனுபவத்துடன், விவசாயிகள் நம்பும் தரம் மற்றும் நம்பகத்தன்மைக்கான பெயரை நாங்கள் உருவாக்கியுள்ளோம்.",
    qualityCertified: "99% விவசாயிகள் திருப்தி",
    fastDelivery: "சீரான சத்துணவு",
    expertSupport: "எப்போதும் கையிருப்பு",
    featuredProducts: "முக்கிய தயாரிப்புகள்",
    allProductsMeet: "தலைமுறைகள் நம்பிக்கையுடன்.",
    timelyDelivery: "ஒவ்வொரு தயாரிப்பும் ஆரோக்கியமான மற்றும் அதிக விளைவுக்கும் தயாரிப்பு.",
    technicalGuidance: "எந்த பருவமாக இருந்தாலும் நம்பகமான விநியோகம்.",
    featuredDesc: "எங்கள் மிகவும் பிரபலமான கால்நடை தீவன தயாரிப்புகள்",
    
    // About Page
    aboutHero: "ராம்சன் ஃபீட்ஸ் பற்றி",
    aboutTagline: "25 ஆண்டுகளுக்கும் மேலாக கால்நடை ஊட்டச்சத்தில் உங்கள் நம்பகமான கூட்டாளி",
    ourStory: "எங்கள் கதை",
    story1: "2000 ஆம் ஆண்டில் நிறுவப்பட்ட ராம்சன் ஃபீட்ஸ், உயர்தரமான மற்றும் மலிவான மாட்டுத் தீவனங்களை உள்ளூர் விவசாயிகளுக்கு வழங்கும் எளிய நோக்கத்துடன் ஒரு சிறிய குடும்ப தொழிலாக தொடங்கியது. சிறிய முயற்சியாகத் தொடங்கிய எங்கள் நிறுவனம், இன்று மாட்டுத் தீவனத் துறையில் மிகவும் நம்பகமான நிறுவனங்களில் ஒன்றாக வளர்ந்துள்ளது.",
    story2: "இன்று, பல மாநிலங்களில் உள்ள நூற்றுக்கணக்கான பண்ணைகளுக்கும் கடைகளுக்கும் எங்கள் தயாரிப்புகளை வழங்குகிறோம். ஆனால் ஆரம்ப காலத்தில் எங்களை வரையறுத்த குடும்ப மதிப்புகள் மற்றும் தரத்திற்கான பற்றுதல் ஆகியவற்றை இன்றும் அதே உறுதியுடன் கடைபிடித்து வருகின்றோம்.",
    ourMission: "எங்கள் நோக்கம்",
    ourVision: "எங்கள் பார்வை",
    missionText: "கால்நடைகளின் ஆரோக்கியம் மற்றும் நலனை ஊக்குவிக்கும் வகையில் வடிவமைக்கப்பட்ட, உயர்தர தீவன தயாரிப்புகள் மூலம் கால்நடை உற்பத்தித்திறன் மற்றும் விவசாயி லாபத்தை மேம்படுத்துவது.",
    visionText: "எங்கள் தரம், நம்பகத்தன்மை மற்றும் வாடிக்கையாளர் அர்ப்பணிப்புக்காக அங்கீகரிக்கப்பட்ட, கால்நடைத் தொழிலுக்கான புதுமையான ஊட்டச்சத்து தீர்வுகளின் முன்னணி வழங்குநராக இருக்க வேண்டும்.",
    ourValues: "எங்கள் மதிப்புகள்",
    valuesDesc: "நாம் செய்யும் அனைத்தையும் வழிநடத்தும் கொள்கைகள்",
    quality: "தரம்",
    innovation: "புதுமை",
    community: "கூட்டுழைப்பு",
    integrity: "நேர்மை",
    qualityd: "தரம் காக்கும் விடாமுயற்சி",
    innovationd: "புதுமையான படைப்பு",
    integrityd: "செயலில் நேர்மை",
    communityd: "இணைந்து வளர்வோம்",
    benefitsTitle: "எங்கள் கால்நடை தீவனத்தின் நன்மைகள்",
    benefitsDesc: "விவசாயிகள் ஏன் தங்கள் கால்நடைகளுக்கு ராம்சன் ஃபீட்ஸை தேர்வு செய்கிறார்கள்",
    improvedHealth: "மேம்பட்ட கால்நடையின் ஊட்டச்சத்து",
    enhancedProductivity: "மேம்பட்ட பால் உற்பத்தி",
    costEffective: "குறைந்த விலை",
    naturalIngredients: "இயற்கையான மூலப்பொருட்கள்",
    yearsExperience: "ஆண்டுகளின் அனுபவம்",
    satisfiedCustomers: "நிறைவடைந்த வாடிக்கையாளர்கள்",
    productsAvailable: "பொருட்கள்",
    customerSupport: "விளைபயன்",
    
    // Products Page
    productsHero: "எங்கள் தயாரிப்புகள்",
    productsDesc: "உகந்த கால்நடை ஆரோக்கியத்திற்கான உயர்தர கால்நடை தீவனம் மற்றும் மூலப்பொருட்கள்",
    nutritionallyBalanced: "கால்நடை வளர்ச்சியின் அனைத்து நிலைகளுக்கும் ஊட்டச்சத்து சமநிலை கொண்ட தீவனங்கள்",
    highQualityIngredients: "தனிப்பயன் தீவன சூத்திரங்களுக்கான உயர்தர மூலப்பொருட்கள்",
    placeOrder: "உங்கள் ஆர்டரை வைக்கவும்",
    orderDesc: "உங்கள் ஆர்டரை உறுதிப்படுத்த WhatsApp மூலம் தொடர்பு கொள்வோம்.",
    fullName: "முழு பெயர்",
    whatsappNumber: "WhatsApp எண்",
    productInterest: "விருப்ப தயாரிப்பு",
    enterProductName: "தயாரிப்பு பெயரை உள்ளிடவும்",
    selectedItems: "தேர்ந்தெடுக்கப்பட்ட பொருட்கள்",
    totalItems: "மொத்த பொருட்கள்",
    clearSelection: "தேர்வை அழிக்கவும்",
    orderProcess: "எங்கள் ஆர்டர் செயல்முறை எவ்வாறு செயல்படுகிறது",
    processStep1: "உங்கள் தேவைகளுடன் ஆர்டர் படிவத்தை பூர்த்தி செய்யவும்",
    processStep2: "விவரங்களை உறுதிப்படுத்த WhatsApp மூலம் தொடர்பு கொள்வோம்",
    processStep3: "ஆர்டர் உறுதிப்படுத்தல் மற்றும் டெலிவரி காலக்கெடுவைப் பெறவும்",
    processStep4: "உங்கள் தரமான தீவனப் பொருட்களை சரியான நேரத்தில் டெலிவரி செய்யுங்கள்",
    toor: "துவரை பொட்டு",
    orid: "உளுந்து டஸ்ட்  ",
    rg: "கடலை பொட்டு",
    moong: "பாசி டஸ்ட்",
    maize: "மக்கா சோளம்",
    masoor: "மசூர்",
    kappi: "கப்பி",
    salt: "உப்பு",
    ricebran: "தவிடு",
    broad: "அவரை பருப்பு",
    sc: "கடலை புண்ணாக்கு",
    pellete: "குச்சி",
    ricef: "அரிசி மாவு",
    pmaize: "மக்கா மாவு",

    toord: "ஆற்றல் நிறைந்த தோல்",
    oridd: "புரதம் நிறைந்த தூள்",
    rgd: "நார்சத்து கொண்ட தோல்",
    moongd: "கனிமம் நிறைந்த தூள்",
    masoord: "வைட்டமின் நிறைந்த பருப்பு",
    maized: "ஆற்றல் தானியம்",
    ricebd: "செரிமான ஆற்றல் மூலம்",
    broadd: "புரதம் நிறைந்த பீன்ஸ்",
    scd: "புரதம் மற்றும் எண்ணெய் மூலம்",
    pelleted: "ஆற்றல் மிக்க துகள்",
    ricefbd: "மென்மையான ஆற்றல் தூள்",
    pmaized: "சுவையான சோளம் தூள்",
    kappid: "நார்சத்து நிறைந்த கழிவு",
    saltd: "கனிம சமநிலை",

    maan: "மான் கலப்பு தீவனம்",
    komatha: "கோமாதா கலப்பு தீவனம்",
    goat: "ராம்சன் ஆடு தீவனம்",
    ramachandra: "ராமச்சந்திரா துவரை தீவனம்",
    ivory: "60Kg ராமச்சந்திரா துவரை தீவனம்",
    no2: "No.2 துவரை தீவனம்",
    spkomatha: "கோமாதா Spl துவரை தீவனம்",
    srtc: "SRTC உளுந்து தீவனம்",
    smr: "SMR உளுந்து தீவனம்",
    apple: "ஆப்பிள் பருப்பு தீவனம்",
    mgs: "MGS பாசி தீவனம்",
    appu: "அப்பு அவரை தீவனம்",

    // Contact Page
    contactHero: "எங்களை தொடர்பு கொள்ள",
    contactDesc: "விசாரணைகள், ஆர்டர்கள் அல்லது ஆதரவிற்காக எங்களைத் தொடர்பு கொள்ளவும்",
    sendUsMessage: "எங்களுக்கு ஒரு செய்தியை அனுப்புங்கள்",
    getInTouch: "தொடர்பில் இருங்கள்",
    visitOffice: "எங்கள் தொழிற்சாலை பார்வையிடவும்",
    businessHours: "வணிக நேரங்கள்",
    emergencyContact: "அவசர தொடர்பு",
    emailUs: "மின்னஞ்சல் அனுப்ப",
    emergencyDesc: "அவசர ஆர்டர்கள் அல்லது வணிக நேரங்களுக்கு புறம்பான ஆதரவிற்கு:",
    verifyHuman: "நீங்கள் மனிதர் என்பதை சரிபார்க்கவும்",
    enterCode: "மேலே உள்ள குறியீட்டை உள்ளிடவும்",
    faq: "அடிக்கடி கேட்கப்படும் கேள்விகள்",
    faqDesc: "பொதுவான கேள்விகளுக்கு விரைவான பதில்கள்",
    ourLocation: "எங்கள் இடம்",
    message: "செய்தி",
    tellUsHow: "உங்கள் செய்தியை கூறுங்கள்",
    deliveryArea: "விநியோக பகுதி",
    deliveryAreaAnswer: "நாங்கள் இந்தியாவின் அனைத்து பகுதிகளுக்கும் விநியோகம் செய்கிறோம்.",
    bulkDiscounts: "மொத்த தள்ளுபடி",
    bulkDiscountsAnswer: "அதிக அளவில் ஆர்டர்கள் செய்வோருக்கு சிறப்பு தள்ளுபடிகள் வழங்கப்படுகின்றன.",
    customFormulations: "தனிப்பயன் கலவை",
    customFormulationsAnswer: "உங்கள் தேவைக்கு ஏற்ப தனிப்பயன் கலவைகள் தயாரிக்கப்படுகின்றன. குறைந்தபட்சம் 100 மூட்டைகள் ஆர்டர் அவசியம்.",
    paymentMethods: "கட்டண முறைகள்",
    paymentMethodsAnswer: "நாங்கள் முன்கூட்டியே பணம் பெற்றுக் கொள்கிறோம்.",

    
    // Footer
    quickLinks: "விரைவு இணைப்புகள்",
    contactInfo: "தொடர்பு தகவல்",
    rightsReserved: "அனைத்து உரிமைகளும் பாதுகாக்கப்பட்டவை",
    companyDescription: "வளர்ச்சி! பால்! வலிமை!"
  }
};

export const TranslationProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');

  const t = (key) => {
    return translations[language][key] || key;
  };

  const switchLanguage = (lang) => {
    setLanguage(lang);
  };

  return (
    <TranslationContext.Provider value={{ t, switchLanguage, language }}>
      {children}
    </TranslationContext.Provider>
  );
};